import React, { useCallback, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import cn from "classnames";

import Popup from "components/common/Popup";
import FormikTextField from "components/InputsField/formikInputs/FormikTextField";
import Button from "components/common/Button";
import { useHttp } from "hooks/http.hook";
import { validation } from "./validate";
import { getProfile } from "store/profile";
import { signInWithToken } from "store/auth";
import { EMPLOYEE_ENDPOINTS } from "endpointsConst";

import styles from "./styles.module.scss";

const RegistrationEmployee = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { request, loading } = useHttp();

    const [passVisible, setPassVisible] = useState(false);
    const [rePassVisible, setRePassVisible] = useState(false);

    const handlePassVisible = () => {
        setPassVisible(!passVisible);
    };
    const handleRePassVisible = () => {
        setRePassVisible(!rePassVisible);
    };

    const handleClose = useCallback(() => {
        navigate("/");
    }, [navigate]);

    const handleSubmit = (data, { setErrors }) => {
        request(EMPLOYEE_ENDPOINTS.PASSWORD, "patch", {
            code: data.code,
            email: data.email,
            password: data.password,
        })
            .then(async (res) => {
                await dispatch(signInWithToken(res.data));
                await dispatch(getProfile());
                navigate("/profile/company");
            })
            .catch((error) => {
                const errors = [];
                if (error.response.status === 404) {
                    errors["email"] = t("employee.add_employee.employeeIsUndefined");
                } else {
                    error.response.data.errors.forEach((error) => {
                        errors[error.path] = t(error.type);
                    });
                }
                setErrors(errors);
            });
    };

    const initialValues = useMemo(() => {
        const query = new URLSearchParams(location.search);
        const code = query.get("code");
        const email = query.get("email");
        return {
            password: "",
            rePassword: "",
            code,
            email,
        };
    }, [location.search]);

    return (
        <Popup title={t("employee.add_employee.create")} onClose={handleClose}>
            <Formik
                initialValues={initialValues}
                validationSchema={validation(t)}
                onSubmit={handleSubmit}
            >
                <Form>
                    <FormikTextField
                        name="code"
                        label={t("registration.code")}
                        wrapperClassName={styles["field--with-margin"]}
                        required
                        disabled
                    />
                    <FormikTextField
                        name="email"
                        label={t("registration.email")}
                        wrapperClassName={styles["field--with-margin"]}
                        required
                        disabled
                    />
                    <FormikTextField
                        name="password"
                        type={passVisible ? "text" : "password"}
                        maxLength="64"
                        label={t("registration.password")}
                        wrapperClassName={styles["field--with-margin"]}
                        additionalComponent={
                            <button
                                type="button"
                                className={cn(styles.eye, passVisible && styles["eye--hide"])}
                                onClick={handlePassVisible}
                            />
                        }
                        required
                    />
                    <FormikTextField
                        name="rePassword"
                        type={rePassVisible ? "text" : "password"}
                        maxLength="64"
                        label={t("registration.repeat_password")}
                        wrapperClassName={styles["field--with-margin"]}
                        additionalComponent={
                            <button
                                type="button"
                                className={cn(styles.eye, rePassVisible && styles["eye--hide"])}
                                onClick={handleRePassVisible}
                            />
                        }
                        required
                    />
                    <div className={styles.button}>
                        <Button type="submit" disabled={loading}>
                            {t("registration.sign_in_button")}
                        </Button>
                    </div>
                </Form>
            </Formik>
        </Popup>
    );
};

export default RegistrationEmployee;
